import { cva, type VariantProps } from 'class-variance-authority';
import type { ElementType, ReactNode, HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

const glassVariants = cva('', {
  variants: {
    variant: {
      base: 'glass-base',
      strong: 'glass-strong',
    },
  },
  defaultVariants: { variant: 'base' },
});

export interface GlassPanelProps
  extends HTMLAttributes<HTMLElement>,
    VariantProps<typeof glassVariants> {
  /**
   * The HTML element / React component to render. Defaults to `div`.
   * Use `as="section"` / `as="nav"` / `as="aside"` to ship semantic landmark roles.
   */
  as?: ElementType;
  children?: ReactNode;
  className?: string;
}

/**
 * `<GlassPanel>` — the foundational glass surface.
 *
 * Single source of truth for the glass aesthetic per UI-SPEC § 1.3. Every
 * other glass surface in the dashboard (cards, tiles, modals, filter bar)
 * composes this component.
 *
 * Variants:
 * - `base`   — surrounding surface (filter bar, card outer, modal backdrop).
 *              Frosted (`backdrop-filter: blur(12px) saturate(1.5)`),
 *              `bg-ocean-900` at 40% alpha, `border-foam-50/10`.
 * - `strong` — text-bearing surface (card text panel, stats tile inner,
 *              modal content). Lighter blur (8px) but a near-solid
 *              `bg-ocean-950/72` scrim for AAA-contrast text. Sets
 *              `isolation: isolate` so the inner backdrop-filter does NOT
 *              compound on top of an outer glass-base parent.
 *
 * Accessibility:
 * - Honours `prefers-reduced-transparency: reduce` via the CSS rules in
 *   `tailwind.css` — the panel renders solid `bg-ocean-900` / `bg-ocean-950`.
 * - Honours `@supports not (backdrop-filter: ...)` for older browsers.
 * - The component does NOT add any `role`, `aria-*`, or focus styles by
 *   itself; consumers attach those (or use `as="section" role="region"`).
 *
 * @example
 *   // Filter bar (sticky outer)
 *   <GlassPanel as="section" variant="base" role="region" aria-label="Filters">
 *     ...
 *   </GlassPanel>
 *
 * @example
 *   // Card composition (base outer, strong text-bearing inner)
 *   <GlassPanel variant="base" className="grid grid-cols-[440px_1fr] h-[330px]">
 *     <HeroPhoto src={url} alt={addr} priority />
 *     <GlassPanel variant="strong" className="p-6">
 *       <h3>{addr}</h3>
 *     </GlassPanel>
 *   </GlassPanel>
 */
export function GlassPanel({
  variant,
  as: Component = 'div',
  className,
  children,
  ...rest
}: GlassPanelProps) {
  return (
    <Component className={cn(glassVariants({ variant }), className)} {...rest}>
      {children}
    </Component>
  );
}
